import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { X } from 'lucide-react';

const getScoreColor = (s) => (s == null ? '#94a3b8' : s >= 80 ? '#16a34a' : s >= 50 ? '#d97706' : '#dc2626');

/**
 * Usage:
 * <AssessmentComparisonModal
 *   jobId={selectedJob._id}
 *   jobRole={selectedJob.role}
 *   onClose={() => setShowComparisonModal(false)}
 * />
 */
export default function AssessmentComparisonModal({ jobId, jobRole, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [questions, setQuestions] = useState([]);
  const [candidates, setCandidates] = useState([]);

  useEffect(() => {
    if (!jobId) return;
    fetchComparison();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId]);

  const fetchComparison = async () => {
    try {
      setLoading(true);
      setError('');
      const res = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/assessment/job/${jobId}`);
      setQuestions(res.data.questions || []);
      setCandidates(res.data.candidates || []);
    } catch (err) {
      console.error('Fetch Job Assessments Error:', err);
      setError(err.response?.data?.message || 'Failed to load assessment comparison');
    } finally {
      setLoading(false);
    }
  };

  if (!jobId) return null;

  const getAnswer = (candidate, questionId) =>
    candidate.answers.find((a) => a.questionId === questionId)?.answer || '';

  const getQScore = (candidate, questionId) =>
    candidate.evaluation.find((e) => e.questionId === questionId)?.score;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '95vw', width: '1200px' }}>
        <div className="modal-header">
          <div>
            <h2>Assessment Comparison</h2>
            <p>{jobRole}</p>
          </div>
          <button className="close-btn" onClick={onClose}><X size={20} /></button>
        </div>

        <div className="modal-body" style={{ padding: 0 }}>
          {loading && <div style={{ padding: '30px', textAlign: 'center', color: '#64748b' }}>Loading candidates...</div>}
          {error && <div style={{ padding: '30px', textAlign: 'center', color: '#dc2626' }}>{error}</div>}

          {!loading && !error && candidates.length === 0 && (
            <div style={{ padding: '30px', textAlign: 'center', color: '#94a3b8' }}>
              No candidates have taken this assessment yet.
            </div>
          )}

          {!loading && !error && candidates.length > 0 && (
            <div style={{ overflowX: 'auto', padding: '0 20px 20px' }}>
              <table style={{ borderCollapse: 'collapse', minWidth: `${560 + questions.length * 220}px` }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #e2e8f0' }}>
                    <th style={thStyle}>Candidate</th>
                    <th style={thStyle}>Email</th>
                    <th style={thStyle}>Status</th>
                    <th style={thStyle}>Score</th>
                    <th style={thStyle}>Submitted</th>
                    {questions.map((q, idx) => (
                    <th
                        key={q.id}
                        style={{ ...thStyle, minWidth: '220px', cursor: 'help' }}
                        title={q.question}
                    >
                        Q{idx + 1}: {q.question.length > 40 ? q.question.slice(0, 40) + '…' : q.question}
                    </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {candidates.map((c) => (
                    <tr key={c.submissionId} style={{ borderBottom: '1px solid #f1f5f9' }}>
                      <td style={{ ...tdStyle, fontWeight: 600, color: '#1e293b' }}>{c.candidateName}</td>
                      <td style={{ ...tdStyle, color: '#64748b' }}>{c.candidateEmail}</td>
                      <td style={tdStyle}>
                        <span style={{
                          fontSize: '0.75rem', fontWeight: 600, padding: '2px 8px', borderRadius: '10px',
                          background: c.status === 'Completed' ? '#f0fdf4' : '#fff7ed',
                          color: c.status === 'Completed' ? '#166534' : '#c2410c'
                        }}>
                          {c.status}
                        </span>
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 700, color: getScoreColor(c.overallScore) }}>
                        {c.overallScore != null ? `${c.overallScore}/100` : '--'}
                      </td>
                      <td style={{ ...tdStyle, color: '#94a3b8', fontSize: '0.8rem' }}>
                        {c.submittedAt ? new Date(c.submittedAt).toLocaleDateString() : '--'}
                      </td>
                      {questions.map((q) => {
                        const answer = getAnswer(c, q.id);
                        const qScore = getQScore(c, q.id);
                        return (
                          <td key={q.id} style={{ ...tdStyle, minWidth: '220px', maxWidth: '220px', verticalAlign: 'top' }}>
                            <div style={{
                              whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.8rem', color: '#334155',
                              fontFamily: q.type === 'coding' ? 'monospace' : 'inherit', maxHeight: '80px', overflowY: 'auto'
                            }}>
                              {answer || <span style={{ color: '#cbd5e1' }}>No answer</span>}
                            </div>
                            {qScore != null && (
                              <div style={{ marginTop: '4px', fontSize: '0.7rem', fontWeight: 700, color: getScoreColor(qScore * 10) }}>
                                {qScore}/10
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

const thStyle = {
  textAlign: 'left', padding: '10px', fontSize: '0.8rem', color: '#64748b',
  position: 'sticky', top: 0, background: '#fff', whiteSpace: 'nowrap'
};

const tdStyle = { padding: '10px', fontSize: '0.85rem' };